<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import ReportFolderButton from '../ReportFolderButton/ReportFolderButton.vue';
import type { TFolder } from '@/_core/entities/report/models/types';
import { useActiveFolderName } from '@/_core/entities/report/utils/report.utils';
import { DEFAULT_FOLDER_NAMES } from '@/_core/entities/report/models/consts';

const props = defineProps<{
  foldersData?: TFolder[];
  isFetchingFolders?: boolean;
}>();

const instance = getCurrentInstance();
const route = computed(() => instance?.proxy.$route);
const router = computed(() => instance?.proxy.$router);

const allVulnsFolder = computed(() =>
  props.foldersData?.find((f) => !f.id && f.name === DEFAULT_FOLDER_NAMES.allVulnerabilities)
);

const totalCount = computed(
  () => allVulnsFolder.value?.statistics.reduce((acc, s) => acc + s.count, 0) ?? 0
);

const folders = computed(
  () => props.foldersData?.filter((f) => !!f.id || f.name === DEFAULT_FOLDER_NAMES.mainReport) ?? []
);

const activeFolderName = useActiveFolderName(route);

function selectFolder(name: string) {
  if (name === activeFolderName.value) return;
  router.value?.push({ query: { ...route.value?.query, folderName: name } });
}
</script>

<template>
  <div class="tw:min-w-14 tw:flex tw:flex-col tw:gap-3 tw:sticky tw:top-4">
    <button
      class="tw:w-full"
      :disabled="isFetchingFolders"
      @click="selectFolder('All Vulnerabilities')"
    >
      <div
        class="tw:p-2 tw:bg-white tw:text-ghost tw:typography-sidenotes tw:active:text-secondary tw:transition-colors tw:rounded-lg tw:lg:hover:bg-ghost/10 tw:outline"
        :class="
          activeFolderName === 'All Vulnerabilities'
            ? 'tw:text-secondary tw:outline-main-blue/30'
            : 'tw:outline-transparent'
        "
      >
        <div class="tw:font-bold tw:leading-5">{{ totalCount }}</div>
        <div>All</div>
      </div>
    </button>

    <div v-for="(folder, index) in folders" :key="index" class="tw:text-center">
      <ReportFolderButton
        :folder="folder"
        :isActive="activeFolderName === folder.name"
        :disabled="isFetchingFolders"
        @click="selectFolder(folder.name)"
      />

      <div class="tw:mt-1 tw:typography-sidenotes tw:text-secondary tw:max-w-16">
        {{ folder.name }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
